import { Card, MenuItem, TextField } from '@mui/material';
import dayjs from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import AllderTable from '../../../components/AllderTable';
import { useGlobalTable } from '../../../hooks/useGlobalTable';
import { packageHandler } from '../../../redux/handlers';
import { createTableColumns } from '../../../utils/createTableColumns';

const TYPE_ACTION_OPTIONS = [
  { value: 'ALL', label: 'ทั้งหมด' },
  { value: 'CREATE', label: 'สร้าง' },
  { value: 'UPDATE', label: 'แก้ไข' },
  { value: 'DELETE', label: 'ลบ' }
];

const TYPE_MAP = {
  CREATE: 'สร้าง',
  UPDATE: 'แก้ไข',
  DELETE: 'ลบ'
};

const TEXTFIELD_STYLES = {
  InputProps: { sx: { borderRadius: '7px' } },
  sx: {
    '.Mui-disabled': {
      background: '#F8F8F9'
    }
  },
  className: 'w-[200px] !ml',
  size: 'small'
};

export function PackageHistoryTab() {
  const dispatch = useDispatch();
  const { packageID } = useParams();
  const [actionType, setActionType] = useState('ALL');

  const fetchHistoryData = useCallback(
    params => {
      const startDate = dayjs().startOf('year').format('YYYY-MM-DD');
      const endDate = dayjs().format('YYYY-MM-DD');

      const queryParams = {
        ...params,
        package_main_id: packageID,
        ...(actionType !== 'ALL' && { type: actionType }),
        start_date: startDate,
        end_date: endDate
      };
      return dispatch(packageHandler.getPackageLogs(queryParams));
    },
    [dispatch, packageID, actionType]
  );

  const {
    data: historyData,
    loading,
    totalRows,
    paginationModel,
    setPaginationModel,
    handleSort,
    refreshData
  } = useGlobalTable({
    fetchData: fetchHistoryData,
    defaultSort: 'created_at',
    defaultOrder: 'desc',
    defaultPageSize: 20
  });

  useEffect(() => {
    if (packageID) {
      refreshData();
    }
  }, [packageID, refreshData]);

  const handleExport = () => {
    if (!packageID) {
      console.warn('Cannot export: package_main_id not available');
      return;
    }

    const startDate = dayjs().startOf('year').format('YYYY-MM-DD');
    const endDate = dayjs().format('YYYY-MM-DD');

    const params = {
      package_main_id: packageID,
      ...(actionType !== 'ALL' && { type: actionType }),
      start_date: startDate,
      end_date: endDate
    };
    dispatch(packageHandler.packageLogToExcel(params));
  };

  const renderUserInfo = row => {
    const user = row.userAdmin;

    if (!user) return '-';

    return (
      <div className="d-flex align-items-center">
        <span>{`${user.prefixName?.name || ''} ${user.first_name || ''} ${user.last_name || ''}`}</span>
      </div>
    );
  };

  const columns = createTableColumns([
    {
      field: 'created_at',
      headerName: 'วัน-เวลา',
      width: 180,
      sortable: true,
      renderCell: ({ row }) => (
        <div className="text-[#2DC76D]">{dayjs(row.created_at).format('DD/MM/YYYY HH:mm')} น.</div>
      )
    },
    {
      field: 'type',
      headerName: 'ประเภท',
      width: 120,
      sortable: false,
      renderCell: ({ row }) => <div>{TYPE_MAP[row.type] || row.type}</div>
    },
    {
      field: 'notes',
      headerName: 'รายละเอียด',
      flex: 1,
      minWidth: 300,
      sortable: false,
      renderCell: ({ row }) => <div dangerouslySetInnerHTML={{ __html: row.notes }} />
    },
    {
      field: 'userAdmin',
      headerName: 'ผู้ดำเนินการ',
      width: 180,
      sortable: false,
      renderCell: ({ row }) => renderUserInfo(row)
    }
  ]);

  return (
    <Card className="!rounded-lg mb-4">
      <div className="py-4">
        <div className="d-flex align-items-center px-4 mb-4">
          <button className="btn btn-export-st-1" onClick={handleExport} disabled={!packageID}>
            Export
          </button>

          <TextField
            {...TEXTFIELD_STYLES}
            select
            label="เลือกดูตามประเภท"
            value={actionType}
            onChange={e => setActionType(e.target.value)}
            className="me-auto w-[200px] !ml-7"
          >
            {TYPE_ACTION_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </div>

        <AllderTable
          data={historyData}
          loading={loading}
          columns={columns}
          totalRows={totalRows}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          handleSort={handleSort}
        />
      </div>
    </Card>
  );
}
